import { Link as RouterLink } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import type { Artwork } from "../../types/artwork";
import { getArtworkImage } from "../../lib/artworkImages";
import { radius, media } from "../../styles/tokens";
import { useLanguage } from "../../context/LanguageContext";
import ArtworkStatusRibbon from "../BannerRibbon";
import { useSiteTheme } from "../../theme/ThemeContext";

interface ArtworkCardProps {
  artwork: Artwork;
}

function ArtworkCard({ artwork }: ArtworkCardProps) {
  const { id, title } = artwork;
  const { language } = useLanguage();
  const { palette } = useSiteTheme();
  const shortDescription =
    artwork.short_description[language]?.trim() || "View artwork";
  const ribbonText = artwork.is_sold
    ? "SOLD"
    : Number(artwork.price) === 0
      ? "NOT FOR SALE"
      : null;

  return (
    <Box
      component={RouterLink}
      to={`/artworks/${id}`}
      sx={{
        display: "block",
        width: "100%",
        textDecoration: "none",
        color: "inherit",
        borderRadius: radius.card,
        overflow: "hidden",
        backgroundColor: palette.paper,
        border: "1px solid",
        borderColor: "divider",
        transition: "transform 0.25s ease",
        "&:hover": {
          transform: "translateY(-4px)",
        },
        "&:hover .artwork-image": {
          transform: "scale(1.04)",
        },
        "&:hover .artwork-hover-label": {
          opacity: 1,
          transform: "translateY(0)",
        },
      }}
    >
      <Box
        sx={{
          p: 1.5,
          pb: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            aspectRatio: media.artworkCardAspectRatio,
            overflow: "hidden",
            borderRadius: radius.card,
          }}
        >
          <Box
            component="img"
            src={getArtworkImage(artwork, 400)}
            alt={title[language]}
            className="artwork-image"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              transition: "transform 0.45s ease",
              position: "relative",
              overflow: "hidden",
            }}
          />
          <ArtworkStatusRibbon
            isSold={artwork.is_sold}
            price={artwork.price}
            variant="card"
          />
          <Box
            className="artwork-hover-label"
            sx={{
              position: "absolute",
              left: 2,
              bottom: 2,
              px: 1.5,
              py: 0.75,
              backgroundColor: palette.footer,
              color: palette.text,
              borderRadius: 10,
              fontSize: "0.75rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              opacity: 0,
              transform: "translateY(6px)",
              transition: "all 0.3s ease",
              zIndex: 2,
            }}
          >
            {shortDescription}
          </Box>
        </Box>

        <Box
          sx={{
            textAlign: "center",
            px: 2,
            pt: 1.5,
            pb: 1.75,
          }}
        >
          <Typography
            sx={{
              fontSize: "1.1rem",
              fontWeight: 400,
              lineHeight: 1.25,
              mb: 0.75,
              color: palette.controlText,
            }}
          >
            {title[language]}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default ArtworkCard;
